import styled from 'styled-components'
import { BlankIcon, EmptyState, Heading } from '@lmc/ui-kit'
import { Divider } from 'antd'
import { useTranslation } from 'react-i18next'
import TeamMemberAccordion from './TeamMemberAccordion'
import EventDetails from './EventDetails'
import { Button } from '@lmc/ui-kit'
import { getQueryParams } from 'utils/getQueryParams'
import { Link } from 'react-router-dom'
import { Text } from '@lmc/ui-kit/src/general'
import { RightOutlined } from '@ant-design/icons'
import { ClockInDetails } from 'screens/Rostering/RotaDrawer/components/DrawerStatus/ClockInDetails'
import { ClockInOutModal } from 'screens/Rostering/RotaDrawer/components/DrawerStatus/ClockInOutModal'
import { useState } from 'react'
import { ClockInTeamMember } from 'screens/Rostering/types'

type CurrentlyClockedInWidgetProps = {
  clockedIns: any[]
}

const CurrentlyClockedInWidget = ({
  clockedIns,
}: CurrentlyClockedInWidgetProps) => {
  const { t } = useTranslation()

  if (!clockedIns) return null

  const [isClockInModalOpen, setIsClockInModalOpen] = useState(false)
  const [teamMember, setTeamMember] = useState<ClockInTeamMember>()

  const openClockInOutModal = (user: any) => {
    const teamMember = {
      teamMemberId: user.memberId,
      carer: { name: user.name },
    } as ClockInTeamMember

    setIsClockInModalOpen(true)
    setTeamMember(teamMember)
  }

  const viewLogsURL = (clockedIn: any): string => {
    const queryParams = getQueryParams({
      resident: clockedIn.event?.resident?.id,
      loggedBy: clockedIn.user?.memberId,
      from: clockedIn.event?.startTime,
      to: clockedIn.event?.endTime,
      isPreciseDateTime: true,
    })

    return `/logs?${queryParams}`
  }

  return (
    <Wrapper>
      <Heading level={3}>{t('CURRENTLY_CLOKED_IN')}</Heading>
      <StyledDivider />
      {clockedIns.length === 0 && (
        <EmptyState
          icon={<BlankIcon />}
          title={t('EMPTY_CURRENTLY_CLOCKED_IN')}
          description={t('EMPTY_CURRENTLY_CLOCKED_IN_DESC')}
        />
      )}
      {clockedIns.map((clockedIn) => (
        <TeamMemberAccordion
          key={clockedIn.user.id}
          user={clockedIn.user}
          warnings={clockedIn.warnings}
          status={clockedIn.status}
        >
          <Container>
            <StyledDivider />
            <EventDetails event={clockedIn.event} />
            <ClockInDetails
              time={clockedIn.clockInTime}
              note={clockedIn.note}
              location={clockedIn.location}
              action="edit" // TODO: Need to discuss if this can ever be a manual clock in
              warnings={clockedIn.warnings}
              openClockInOutModal={() => openClockInOutModal(clockedIn.user)}
              timezone={clockedIn.event?.timezone}
            />
            <StyledDivider />
            <Link to={viewLogsURL(clockedIn)} target="_blank">
              <Button variant="primary">
                <Text weight="medium">{t('VIEW_LOGS_CREATED')} </Text>
                <RightOutlined />
              </Button>
            </Link>
            {isClockInModalOpen && (
              <ClockInOutModal
                open={isClockInModalOpen}
                onClose={() => setIsClockInModalOpen(false)}
                revisionDetail={clockedIn.event}
                action="edit"
                type="in"
                teamMember={teamMember}
              />
            )}
          </Container>
        </TeamMemberAccordion>
      ))}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
  flex: 1;
  height: fit-content;
  min-height: 416px;
`
const StyledDivider = styled(Divider)`
  margin: 0;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 16px 16px;
`

export default CurrentlyClockedInWidget
